/**
 * Brand validation utility - Ensures valid brand ID usage
 * Fixes runtime errors from invalid stored brand IDs
 */

const VALID_BRAND_ID = 'test_brand'; // The only brand that exists in the database
const API_BASE = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';

/**
 * Clear all invalid brand IDs from storage and return valid one
 */
export const clearInvalidBrandIds = (): string => {
  try {
    // Clear all possible invalid stored values
    localStorage.removeItem('selectedBrandId');
    localStorage.removeItem('brand_id');
    sessionStorage.removeItem('brand_id');
    
    // Set the valid brand ID
    localStorage.setItem('selectedBrandId', VALID_BRAND_ID);
    localStorage.setItem('brand_id', VALID_BRAND_ID);
    sessionStorage.setItem('brand_id', VALID_BRAND_ID);
    
    console.log('🔧 Cleared invalid brand IDs, using:', VALID_BRAND_ID);
    return VALID_BRAND_ID;
  } catch (error) {
    console.error('Error clearing brand IDs:', error);
    return VALID_BRAND_ID;
  }
};

/**
 * Validate brand ID against known valid brands
 */
export const validateBrandId = async (brandId: string): Promise<string> => {
  try {
    // Quick validation - if it's already the valid one, return it
    if (brandId === VALID_BRAND_ID) {
      return brandId;
    }
    
    // Try to fetch brands from API to validate
    const response = await fetch(`${API_BASE}/api/v1/brands`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      const validBrands = data.brands || data;
      
      // Check if the provided brand ID exists
      const brandExists = validBrands.some((brand: any) => brand.brand_id === brandId);
      
      if (brandExists) {
        return brandId;
      } else {
        // Invalid brand ID - clear storage and use valid one
        console.warn(`🚨 Invalid brand ID detected: ${brandId}, switching to ${VALID_BRAND_ID}`);
        return clearInvalidBrandIds();
      }
    } else {
      // API call failed - fall back to valid brand
      console.warn('Could not validate brand ID via API, using fallback');
      return clearInvalidBrandIds();
    }
  } catch (error) {
    console.error('Error validating brand ID:', error);
    return clearInvalidBrandIds();
  }
};

/**
 * Get a guaranteed valid brand ID
 */
export const getValidBrandId = async (): Promise<string> => {
  // First, clear any invalid stored values
  clearInvalidBrandIds();
  
  // Try to get from storage (now should be valid)
  const storedBrandId = localStorage.getItem('selectedBrandId') || 
                        localStorage.getItem('brand_id') ||
                        sessionStorage.getItem('brand_id');
  
  if (storedBrandId) {
    return await validateBrandId(storedBrandId);
  }
  
  return VALID_BRAND_ID;
};

/**
 * Force brand ID cleanup on app load
 */
export const initializeBrandValidation = () => {
  // Check for common invalid brand IDs and clear them
  const invalidPatterns = [
    'brand_280ac54c7dbb9bf0d1ea908c59edeb7c',
    'brand_57a5dbca9f05f75a',
    'dev_brand',
    'demo_brand'
  ];
  
  const currentBrandId = localStorage.getItem('selectedBrandId') || 
                         localStorage.getItem('brand_id') ||
                         sessionStorage.getItem('brand_id');
  
  if (currentBrandId && (invalidPatterns.includes(currentBrandId) || currentBrandId !== VALID_BRAND_ID)) {
    console.log(`🔧 Detected invalid brand ID: ${currentBrandId}, clearing...`);
    clearInvalidBrandIds();
  }
};

export default {
  clearInvalidBrandIds,
  validateBrandId,
  getValidBrandId,
  initializeBrandValidation,
  VALID_BRAND_ID
};